import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import { rgba } from 'polished';

import { Avatar, Button, Empty, Heading, Paragraph, Popover, Skeleton } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import Preview from 'goals/group-selector/Preview';

const Name = styled(Box)`
	font-weight: bold;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const Footnote = styled(Paragraph)`
	font-size: 12px;
	line-height: 15px;
`;

const Container = styled(Flex)<{ $enabled?: boolean; $selected?: boolean; $color: string }>`
	border-radius: 5px;
	background: ${(props) => props.theme.colors.white};
	box-shadow:
		0px 0px 0px 1px ${(props) => rgba(props.theme.colors.slateBlue, 0.25)},
		0px 2px 4px ${(props) => rgba(props.theme.colors.slateBlue, 0.25)};
	width: 180px;
	height: 48px;
	padding: 0px 10px 0px 4px;
	font-size: 14px;
	line-height: 14px;
	align-items: center;
	border-left: 4px solid ${(props) => props.theme.colors[props.$color]};

	${(props) =>
		props.$enabled !== false &&
		`
	cursor: pointer;

	&:hover {
		box-shadow: 0px 0px 0px 1px ${rgba(props.theme.colors.slateBlue, 0.5)},
			0px 2px 4px ${rgba(props.theme.colors.slateBlue, 0.25)};
	}`}

	${(props) =>
		props.$selected &&
		`
		${Footnote} {
			color: ${rgba(props.theme.colors.white, 0.5)};
		}
		
		&, &:hover {
			background: ${props.theme.colors.primary};
			color: ${props.theme.colors.white};
			border-left-color: ${rgba(props.theme.colors.white, 0.5)};
			box-shadow: 0px 0px 0px 2px ${rgba(props.theme.colors.primary, 0.25)};
		}
	`}
`;

const Main = styled(Box)`
	border-right: 1px solid ${(props) => props.theme.colors.lightBlue};
`;

const Grid = styled(Flex)`
	padding: 16px;
	border-radius: 5px;
	border: 1px solid ${(props) => props.theme.colors.lightBlue};
	align-items: center;
	flex-direction: column;
`;

export default function Role(
	props: ({ user: graphql.User } | { group: graphql.Group }) & {
		transitive?: boolean;
		inverse?: boolean;
		indirect?: boolean;
		me: graphql.User;
		role: graphql.Role;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		refetch: () => Promise<any>;
	},
) {
	return 'user' in props ? (
		<UserRole
			transitive={props.transitive}
			indirect={props.indirect}
			inverse={props.inverse}
			me={props.me}
			user={props.user}
			role={props.role}
			refetch={props.refetch}
		/>
	) : (
		<GroupRole
			indirect={props.indirect}
			me={props.me}
			role={props.role}
			refetch={props.refetch}
			group={props.group}
		/>
	);
}

function UserRolePopover(props: {
	indirect?: boolean;
	transitive?: boolean;
	source: graphql.User;
	target: graphql.User;
	role: graphql.Role;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	refetch: () => Promise<any>;
}) {
	const { t } = useTranslation();

	const [revoke] = graphql.useRevokeRoleMutation();

	return (
		<Box p={3} width={300}>
			<Heading mb={2}>{t(`roles.${props.role.id}`)}</Heading>
			<Paragraph mb={3}>
				{t(`roles.explanations.${_.camelCase(props.role.id)}`, {
					name: props.source.firstName,
					target: props.target.firstName,
				})}
			</Paragraph>
			{props.indirect && (
				<Paragraph mb={3}>
					{t(`roles.explanations.transitive.suffix`, { target: props.target.firstName })}
				</Paragraph>
			)}
			{props.transitive ? (
				<Grid>
					<Empty
						description={t(`employees.settings.roles.revokeImpossible`, {
							name: props.target.firstName,
						})}
					/>
				</Grid>
			) : (
				<Flex>
					<Button
						type="destructive"
						title={t('employees.settings.roles.revokeButton')}
						onClick={async () => {
							await revoke({
								variables: {
									input: {
										userID: props.source.id,
										roleID: props.role.id,
										targetID: props.target.id,
										targetType: graphql.RoleTargetType.User,
									},
								},
							});

							await props.refetch();
						}}
					/>
				</Flex>
			)}
		</Box>
	);
}

function UserRole(props: {
	transitive?: boolean;
	indirect?: boolean;
	inverse?: boolean;
	me: graphql.User;
	role: graphql.Role;
	user: graphql.User;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	refetch: () => Promise<any>;
}) {
	const { t } = useTranslation();

	const [visible, setVisible] = useState(false);

	return (
		<Popover.Target
			visible={visible}
			onDismiss={() => setVisible(false)}
			contents={
				<UserRolePopover
					transitive={props.transitive}
					indirect={props.indirect}
					source={props.inverse ? props.user : props.me}
					target={props.inverse ? props.me : props.user}
					role={props.role}
					refetch={props.refetch}
				/>
			}
		>
			<Container onClick={() => setVisible(true)} $color="employee" $selected={visible}>
				<Avatar user={props.user} size={32} mr={2} />
				<Box>
					<Name>
						{props.user.firstName} {props.user.lastName}
					</Name>
					<Footnote>
						{t(`roles.${props.role.id}`)}
						{(props.indirect || props.transitive) && '*'}
					</Footnote>
				</Box>
			</Container>
		</Popover.Target>
	);
}

function GroupRole(props: {
	indirect?: boolean;
	me: graphql.User;
	role: graphql.Role;
	group: graphql.Group;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	refetch: () => Promise<any>;
}) {
	const { t } = useTranslation();

	const [visible, setVisible] = useState(false);

	const [revoke] = graphql.useRevokeRoleMutation();

	return (
		<Popover.Target
			visible={visible}
			onDismiss={() => setVisible(false)}
			contents={
				<Flex>
					<Main p={3} width={240}>
						<Heading mb={2}>{t(`roles.${props.role.id}`)}</Heading>
						<Paragraph mb={3}>
							{t(`roles.explanations.${_.camelCase(props.role.id)}`, {
								name: props.me.firstName,
								target: props.group.name,
							})}
						</Paragraph>
						{props.indirect && (
							<Paragraph mb={3}>
								{t(`roles.explanations.transitive.suffix`, { target: props.group.name })}
							</Paragraph>
						)}
						<Flex>
							<Button
								type="destructive"
								title={t('employees.settings.roles.revokeButton')}
								onClick={async () => {
									await revoke({
										variables: {
											input: {
												userID: props.me.id,
												roleID: props.role.id,
												targetID: props.group.id,
												targetType:
													props.group.type === graphql.GroupType.Department
														? graphql.RoleTargetType.Department
														: graphql.RoleTargetType.Team,
											},
										},
									});

									await props.refetch();
								}}
							/>
						</Flex>
					</Main>
					<Preview group={props.group} />
				</Flex>
			}
		>
			<Container onClick={() => setVisible(true)} $color="manager" $selected={visible}>
				<Box ml={2}>
					<Name>{props.group.name}</Name>
					<Footnote>
						{t(`roles.${props.role.id}`)}
						{props.indirect && '*'}
					</Footnote>
				</Box>
			</Container>
		</Popover.Target>
	);
}

function RoleSkeleton() {
	return <Skeleton.Box width={180} height={48} />;
}

Role.Skeleton = RoleSkeleton;
